/**
 * Extraction of query parameters from the parent page URL.
 *
 * Ported from geta-forms-frontend/src/Utils/urlParameterExtractor.ts so the bot and
 * the form derive identical field names from an identical landing URL.
 */

// Names the session and the lead pipeline own. Nesting the bag under `url_params` already
// stops a collision with the bot's own /query params, but not with the context keys the
// service identifies the visitor by — a landing URL setting visitor_email or contact_id
// would bind the session to someone else's contact. Kept in step with RESERVED_KEYS in
// conversational-service-node/src/utils/url_params.ts, which drops them again server-side.
// email / mobile_phone_number / first_name are the aliases Context.set writes alongside
// the visitor_* fields, so an email-tool prefill link (?email=*|EMAIL|*) reaches the contact
// through them.
const RESERVED_KEYS = new Set([
  "session_id", "updatedat", "channel", "module", "entry_point", "module_id", "botid",
  "identity_id", "site_id", "visitor_id", "contact_id", "otp", "matomo",
  "visitor_email", "visitor_phone", "visitor_name",
  "email", "mobile_phone_number", "first_name",
  "form_id", "web_form_id", "application_form_id", "facebook_form_id",
]);

/**
 * Safely extracts URL parameters from a parent URL string
 * @param parentUrl - The parent URL containing query parameters
 * @returns Object containing decoded key-value pairs
 */
export const extractUrlParameters = (
  parentUrl: string | undefined
): Record<string, string> => {
  if (!parentUrl) {
    return {};
  }

  let queryString = "";
  try {
    const url = new URL(parentUrl);
    // Hash-router pages carry their query inside the fragment (#/page?utm_source=x), and
    // a route anchor can follow it (…?utm_source=x#pricing) — trim there, or the anchor
    // lands inside the last value. url.search needs no trimming; the parser already
    // excludes the fragment.
    const hashQuery = url.hash.includes("?")
      ? url.hash.slice(url.hash.indexOf("?") + 1).split("#")[0]
      : "";
    // Both sections, not whichever is non-empty first: a page can carry its own top-level
    // query and still take the ad params in the fragment. search goes last so it wins a
    // duplicate key — ad platforms append there, so that copy is the authoritative one.
    queryString = [hashQuery, url.search.slice(1)].filter(Boolean).join("&");
  } catch {
    // parentUrl isn't an absolute URL — an embed snippet may pass any string as
    // windowLocation (geta-tracking-scripts/src/index.ts), not just location.href.
    const queryStart = parentUrl.indexOf("?");
    if (queryStart === -1) {
      return {};
    }
    queryString = parentUrl.slice(queryStart + 1).split("#")[0];
  }

  const keyValueObject: Record<string, string> = {};
  // Assignment, not Object.fromEntries — the inherited __proto__ setter swallows a
  // "__proto__" param, whereas fromEntries would define it as a real own key.
  new URLSearchParams(queryString).forEach((value, key) => {
    if (!value || !isValidParameterKey(key) || RESERVED_KEYS.has(key.toLowerCase())) {
      return;
    }
    keyValueObject[key] = value;
  });

  return keyValueObject;
};

/**
 * Validates parameter keys to prevent potential security issues
 * @param key - The parameter key to validate
 * @returns true if the key is valid, false otherwise
 */
const isValidParameterKey = (key: string): boolean => {
  // Allow alphanumeric characters, underscores, hyphens, and dots
  // This prevents potential XSS through malicious parameter names
  const validKeyPattern = /^[a-zA-Z0-9._-]+$/;
  return validKeyPattern.test(key) && key.length <= 100; // Reasonable length limit
};

/**
 * Serialises the parent-page parameters into the single `url_params` transport value.
 *
 * Nesting the whole bag under one param is deliberate: a flat merge would let a
 * parent URL carrying `?query=`, `?session_id=` or `?language=` overwrite the bot's
 * own protocol params on GET /query. Nested, that collision is impossible — RESERVED_KEYS
 * covers the separate case of colliding with a session context key once the service
 * merges the bag, and the service drops them again regardless.
 *
 * Returns null when there is nothing to send, so the param is omitted entirely.
 */
export const buildUrlParamsPayload = (
  parentUrl: string | undefined
): string | null => {
  const params = extractUrlParameters(parentUrl);
  if (Object.keys(params).length === 0) {
    return null;
  }

  try {
    return JSON.stringify(params);
  } catch (error) {
    console.warn("Error serialising URL parameters:", error);
    return null;
  }
};
